/**
 * IndexedDB offline queue — capsule e segnalazioni tutela.
 * MVP senza dipendenza idb; upgrade-safe.
 */

const DB_NAME = "6amico_offline_v1";
const DB_VERSION = 1;

const STORES = {
  capsules: "capsules_pending",
  segnalazioni: "segnalazioni_pending",
  meta: "sync_meta",
};

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB non disponibile"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORES.capsules)) {
        const s = db.createObjectStore(STORES.capsules, { keyPath: "id" });
        s.createIndex("createdAt", "createdAt", { unique: false });
        s.createIndex("comune_slug", "comune_slug", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.segnalazioni)) {
        const s = db.createObjectStore(STORES.segnalazioni, { keyPath: "id" });
        s.createIndex("createdAt", "createdAt", { unique: false });
        s.createIndex("comuneSlug", "comuneSlug", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: "key" });
      }
    };
  });
}

function tx(storeName, mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        let result;
        try {
          result = fn(store);
        } catch (e) {
          reject(e);
          return;
        }
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error);
      })
  );
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function newOfflineId(prefix = "off") {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export async function addPendingCapsule(record) {
  const entry = {
    id: record.id || newOfflineId("cap"),
    createdAt: record.createdAt || new Date().toISOString(),
    ...record,
  };
  await tx(STORES.capsules, "readwrite", (store) => store.put(entry));
  return entry;
}

export async function listPendingCapsules() {
  return tx(STORES.capsules, "readonly", (store) =>
    reqToPromise(store.index("createdAt").getAll()).then((rows) =>
      (rows || []).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
    )
  );
}

export async function removePendingCapsule(id) {
  return tx(STORES.capsules, "readwrite", (store) => store.delete(id));
}

export async function addPendingSegnalazione(record) {
  const entry = {
    id: record.id || newOfflineId("seg"),
    createdAt: record.createdAt || new Date().toISOString(),
    ...record,
  };
  await tx(STORES.segnalazioni, "readwrite", (store) => store.put(entry));
  return entry;
}

export async function listPendingSegnalazioni() {
  return tx(STORES.segnalazioni, "readonly", (store) =>
    reqToPromise(store.getAll()).then((rows) =>
      (rows || []).sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)))
    )
  );
}

export async function removePendingSegnalazione(id) {
  return tx(STORES.segnalazioni, "readwrite", (store) => store.delete(id));
}

export async function getSyncMeta(key) {
  try {
    return await tx(STORES.meta, "readonly", (store) => reqToPromise(store.get(key)));
  } catch {
    return null;
  }
}

export async function setSyncMeta(key, value) {
  return tx(STORES.meta, "readwrite", (store) =>
    store.put({ key, value, updatedAt: new Date().toISOString() })
  );
}

export { STORES };
